import { Card, CardContent, CardGroup } from 'components/Card'
import constants from 'config/constants'
import usePermission from 'hooks/usePermission'
import * as R from 'ramda'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Icon, Image, Input } from 'semantic-ui-react'

export default function IntegrationIndex({ integrations }: IntegrationIndexPropsType) {
  const [filteredIntegrations, setFilteredConnectors] = useState<Integration[]>(integrations)

  function handleSearchChange(_, { value }) {
    const matchedIntegration = integrations.filter((integration) =>
      R.contains(R.toLower(value), R.toLower(integration.name))
    )
    setFilteredConnectors(matchedIntegration)
  }

  return (
    <div>
      <Input
        icon="far fa-magnifying-glass"
        placeholder="Search integrations"
        iconPosition="left"
        fluid
        onChange={handleSearchChange}
      />
      <ConnectorCards filteredIntegrations={filteredIntegrations} />
    </div>
  )
}

function ConnectorCards({ filteredIntegrations }: ConnectorCardsPropsType) {
  if (filteredIntegrations.length === 0) {
    return (
      <div className="relative" style={{ height: '400px' }}>
        <NoIntegration />
      </div>
    )
  }

  const maximumItemsPerRow = 4

  function resizeImageURL(imageUrl: string): string {
    const defaultImage = 'https://react.semantic-ui.com/images/wireframe/image.png'
    if (!imageUrl) {
      return defaultImage
    }

    const url = new URL(imageUrl)
    const params = new URLSearchParams(url.search)
    params.append('size', 'x116')
    return `${url.origin}${url.pathname}?${params.toString()}`
  }

  return (
    <div className="mt-8">
      <CardGroup itemsPerRow={maximumItemsPerRow} doubling>
        {filteredIntegrations.map((integration, index) => {
          if (integration.deprecated && !integration.is_authenticated) {
            return null
          } else {
            const integrationUrl = integration.url || integration.external_url
            const key = `${index}-${integration.name}-${integrationUrl}`
            return (
              <CardLink
                key={key}
                integrationUrl={integration.url || integration.external_url}
                cardKey={key}
              >
                <CardContent
                  key={`${key}-content`}
                  className="!flex !flex-row items-center p-4 relative justify-center md:!justify-start"
                >
                  {integration.is_authenticated && (
                    <i className="absolute fas fa-circle-check" style={{ top: '10px', right: '10px', zIndex: 1, color: '#4EC677' }} key={`${key}-check`} />
                  )}
                  <Image
                    key={`${key}-image`}
                    src={resizeImageURL(integration.image)}
                    width="64px"
                  />
                  <span
                    key={`${key}-depreciated`}
                    className="hidden font-bold md:block"
                    style={{ paddingLeft: '18px' }}
                  >
                    {integration.deprecated ? integration.name + ' (deprecated)' : integration.name}
                  </span>
                </CardContent>
              </CardLink>
            )
          }
        })}
      </CardGroup>
    </div>
  )
}

function CardLink({ integrationUrl, children, cardKey }) {
  const { hasPermission } = usePermission()
  const hasManageIntegrationsPermission = hasPermission(constants.permissions.manageIntegrations)

  function getPathName(url: string): string {
    if (url.startsWith('/')) {
      return url
    }
    try {
      const urlObj = new URL(url)
      return urlObj.pathname + urlObj.search
    } catch {
      return url
    }
  }

  const style = { color: 'rgba(0,0,0,.68)' }

  let isSameHostname = false
  try {
    const urlObj = new URL(integrationUrl, window.location.origin)
    isSameHostname = urlObj.hostname === window.location.hostname
  } catch {
    // If URL parsing fails, assume it's not the same hostname
  }

  const isInternalLink = Boolean(
    integrationUrl.startsWith('/') ||
      integrationUrl.startsWith(window.location.origin) ||
      isSameHostname
  )

  if (!hasManageIntegrationsPermission) {
    return (
      <a
        key={`${cardKey}-link`}
        className={`ui card ${!hasManageIntegrationsPermission ? 'disabled' : ''}`}
        href={undefined} // Don't add href when disabled
        target="_blank"
        rel="noopener"
      >
        {children}
      </a>
    )
  } else if (isInternalLink) {
    return (
      <Card key={`${cardKey}-card`} as={Link} to={getPathName(integrationUrl)} style={style}>
        {children}
      </Card>
    )
  } else {
    return (
      <a
        key={`${cardKey}-link`}
        className="ui card"
        href={integrationUrl}
        target="_blank"
        rel="noopener"
        style={style}
      >
        {children}
      </a>
    )
  }
}

function NoIntegration() {
  return (
    <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center flex-column">
      <Icon name="inbox" size="huge" className="text-grey-lighter" />
      <span className="font-bold text-grey h4">No results found</span>
      <span className="text-grey h5">
        Try adjusting your search to find what you’re looking for.
      </span>
    </div>
  )
}

type Integration = {
  name: string
  image: string
  is_authenticated: boolean
  url: string | null
  external_url: string | null
  deprecated: boolean
}

type ConnectorCardsPropsType = {
  filteredIntegrations: Integration[]
}

type IntegrationIndexPropsType = {
  integrations: Integration[]
}



// WEBPACK FOOTER //
// ./src/components/IntegrationIndex/index.tsx